import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { SERVICES, getContinent, getCountry } from "@/data/destinations";
import { getCityContent } from "@/data/cityContent";
import { useLang } from "@/i18n/LanguageContext";

export default function CountryPage() {
  const { countrySlug } = useParams();
  const { lang, t } = useLang();
  const country = getCountry(countrySlug);

  if (!country) return <Navigate to="/" replace />;
  const continent = getContinent(country.continent);

  return (
    <main data-testid="country-page">
      <Navbar />

      <section className="relative flex min-h-[72vh] items-end overflow-hidden">
        <img src={country.img} alt={country.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050814] via-[#050814]/45 to-[#050814]/30" />
        <div className="relative mx-auto w-full max-w-[1500px] px-6 pb-16 pt-40 lg:px-12">
          <Link to="/" data-testid="country-back-link" className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 transition-colors hover:text-[#d4af37]">
            <ArrowLeft size={14} /> {t("country.back")}
          </Link>
          <div className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#d4af37]" data-testid="country-continent">
            {t(`cont.${continent.id}`)} — {country.lat.toFixed(2)}° / {country.lon.toFixed(2)}°
          </div>
          <h1 className="mt-4 font-serif text-6xl font-light tracking-tight text-white sm:text-7xl lg:text-8xl" data-testid="country-title">
            <span className="mask-line">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {country.name}
              </motion.span>
            </span>
          </h1>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.3em] text-white/50" data-testid="country-cities-count">
            {t("country.citiesCount", { count: country.cities.length })}
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-[1500px] gap-12 px-6 lg:grid-cols-3 lg:px-12">
          <Reveal>
            <p className="eyebrow">{t("country.overview")}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2">
            <p className="max-w-2xl font-serif text-2xl font-light leading-relaxed text-white/85 lg:text-3xl" data-testid="country-intro">
              {t("country.intro", { country: country.name })}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <Reveal>
            <h2 className="mb-10 font-serif text-3xl font-light text-white lg:text-4xl">{t("country.cities")}</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {country.cities.map((city, i) => (
              <Reveal key={city.slug} delay={i * 0.08}>
                <Link
                  to={`/destinations/${country.slug}/${city.slug}`}
                  data-testid={`city-card-${city.slug}`}
                  className="group relative block overflow-hidden border border-white/10 transition-colors duration-500 hover:border-[#d4af37]/50"
                >
                  <div className="relative h-72 overflow-hidden">
                    <img src={city.img} alt={city.name} loading="lazy" className="img-spotlight h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050814] via-[#050814]/25 to-transparent" />
                    {city.airport && (
                      <span className="absolute left-4 top-4 border border-white/25 bg-[#050814]/70 px-2.5 py-1 font-mono text-[10px] tracking-[0.25em] text-[#e8cf7a] backdrop-blur-sm">
                        {city.airport}
                      </span>
                    )}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
                    <div className="min-w-0">
                      <span className="font-serif text-2xl font-light text-white">{city.name}</span>
                      {getCityContent(city.slug, lang)?.tagline && (
                        <div className="mt-1 truncate font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                          {getCityContent(city.slug, lang).tagline}
                        </div>
                      )}
                    </div>
                    <ArrowUpRight size={16} className="shrink-0 text-[#d4af37] transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-24">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <Reveal>
            <h2 className="mb-10 font-serif text-3xl font-light text-white lg:text-4xl">{t("country.services")}</h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-3 lg:grid-cols-9" data-testid="country-services-strip">
            {SERVICES.map((s) => (
              <div key={s.id} className="flex flex-col items-center gap-3 bg-[#050814] px-3 py-8 text-center">
                <span className="h-1 w-6 bg-[#d4af37]" />
                <span className="text-xs font-light text-white/70">{t(`svc.${s.id}.t`)}</span>
              </div>
            ))}
          </div>
          <Reveal delay={0.15}>
            <Link
              to="/#concierge"
              data-testid="country-cta"
              className="btn-gold group mt-14 inline-flex items-center gap-3 px-8 py-4 text-sm font-semibold tracking-wide"
            >
              {t("country.cta", { country: country.name })}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
